"use client";

import useCompetitionByPageUrl from "@/app/hooks/useCompetitionByPageUrl";
import { useGetDailyPrizesQuery } from "@/app/redux/features/api/lifestyle";
import type { LifestylePrize, PrizeSearchResponse } from "@/app/redux/features/api/lifestyle.types";
import { useEffect, useMemo, useRef } from "react";
import { getLifestyleDevPageUrl } from "../lifestyle-dev";

export interface MultibuyInfo {
  isFirstPurchase: boolean;
  lowerQuantity: number | null;
  upperQuantity: number | null;
  lowerPercent: number | null;
  upperPercent: number | null;
}

export interface LifestyleCompetition {
  /** The competition the page is scoped to. 0 until resolved. */
  competitionId: number;
  /** Comma-separated ids sent as `activeCompetitions` to the search endpoint. */
  activeCompetitions: string;
  /** When the page-scoped competition closes; null until resolved. */
  endDateTime: string | null;
}

/**
 * Resolves which competition this lifestyle repository page is scoped to by
 * matching the page URL against `PageUrl` from `/getactivecompetitions`
 * (via Redux). In Next dev, `getLifestyleDevPageUrl()` can override the fixed
 * `/prizes/lifestyle` route — see `lifestyle-dev.ts`.
 */
export function useLifestyleCompetition(): LifestyleCompetition {
  const pageUrl = getLifestyleDevPageUrl();
  const competition = useCompetitionByPageUrl(pageUrl);
  console.log({ competition });
  return useMemo(() => {
    if (!competition?.CompetitionId) {
      return { competitionId: 0, activeCompetitions: "", endDateTime: null };
    }

    return {
      competitionId: competition.CompetitionId,
      activeCompetitions: String(competition.CompetitionId),
      endDateTime: competition.EndDateTime || competition.EndDate || null,
    };
  }, [competition]);
}

const EMPTY_PRIZES: LifestylePrize[] = [];

function readMultibuy(response?: PrizeSearchResponse): MultibuyInfo | null {
  if (!response) return null;
  return {
    isFirstPurchase: response.IsMultiBuyFirstPurchase,
    lowerQuantity: response.MultibuyLowerQuantity,
    upperQuantity: response.MultibuyUpperQuantity,
    lowerPercent: response.MultibuyLowerPercent,
    upperPercent: response.MultibuyUpperPercent,
  };
}

/**
 * Loads every prize on the page.
 *
 * The legacy page mounted five AngularJS controllers, one per category, each
 * doing its own `prizesearchdaily` call. We keep the same five requests — the
 * endpoint is category-scoped and Redis-cached per category server-side — but
 * merge the results into a single list, because the 2026 design has no category
 * tabs. RTK Query caches each category independently, so switching filters never
 * refetches.
 */
export default function useLifestylePrizes() {
  const { competitionId, activeCompetitions, endDateTime } = useLifestyleCompetition();
  const skip = competitionId === 0;
  const args = { competitionId, activeCompetitions };

  // Called explicitly rather than in a loop to satisfy the rules of hooks.
  const lifestylecars = useGetDailyPrizesQuery({ ...args, prizeCategory: "lifestylecars" }, { skip });
  const cash = useGetDailyPrizesQuery({ ...args, prizeCategory: "cash" }, { skip });
  const bikes = useGetDailyPrizesQuery({ ...args, prizeCategory: "bikes" }, { skip });
  const other = useGetDailyPrizesQuery({ ...args, prizeCategory: "other" }, { skip });
  const watches = useGetDailyPrizesQuery({ ...args, prizeCategory: "watches" }, { skip });

  const queries = useMemo(
    () => [lifestylecars, cash, bikes, other, watches],
    [lifestylecars, cash, bikes, other, watches]
  );

  const isLoading = skip || queries.some(query => query.isLoading);
  const isFetching = queries.some(query => query.isFetching);
  const error = queries.find(query => query.error)?.error;

  const prizes = useMemo(() => {
    if (skip) return EMPTY_PRIZES;

    // Dedupe by ContentNodeId: a prize can be returned under more than one
    // category when it carries multiple subcategories.
    const byContentNodeId = new Map<number, LifestylePrize>();
    for (const query of queries) {
      for (const prize of query.data?.Results ?? []) {
        if (!byContentNodeId.has(prize.ContentNodeId)) {
          byContentNodeId.set(prize.ContentNodeId, prize);
        }
      }
    }
    return Array.from(byContentNodeId.values());
  }, [queries, skip]);

  const multibuy = useMemo(() => readMultibuy(queries.find(query => query.data)?.data), [queries]);

  const averageRrp = useMemo(() => {
    const values = queries.map(query => query.data?.AverageRrp).filter((rrp): rrp is number => typeof rrp === "number");
    if (values.length === 0) return 0;
    return values.reduce((total, rrp) => total + rrp, 0) / values.length;
  }, [queries]);

  // The daily search occasionally returns nothing on a cold Redis cache. The
  // Angular controller retried once (botb.dailycontroller.js:916); keep that.
  const hasRetried = useRef(false);
  useEffect(() => {
    if (skip || isLoading || isFetching || error) return;
    if (prizes.length > 0 || hasRetried.current) return;

    hasRetried.current = true;
    queries.forEach(query => query.refetch());
  }, [skip, isLoading, isFetching, error, prizes.length, queries]);

  return { prizes, multibuy, averageRrp, isLoading, isFetching, error, competitionId, activeCompetitions, endDateTime };
}
